import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CardNav, { CardNavItem } from "components/CardNav";
import logo from "assets/mysher_logo.svg";

const NAV_ITEMS: CardNavItem[] = [
  {
    label: "Visit Us",
    bgColor: "#171310",
    textColor: "#fff",
    links: [
      { label: "Store", ariaLabel: "Visit the Store", to: "/store" },
      { label: "Birds", ariaLabel: "Meet the Birds", to: "/birds" },
    ],
  },
  {
    label: "Discover",
    bgColor: "#221c17",
    textColor: "#fff",
    links: [
      { label: "Videos", ariaLabel: "Watch Videos", to: "/videos" },
      { label: "Blog", ariaLabel: "Read the Blog", to: "/blog" },
    ],
  },
  {
    label: "Connect",
    bgColor: "#d38f14",
    textColor: "#171310",
    links: [{ label: "Contact", ariaLabel: "Contact Us", to: "/contact" }],
  },
];

// Only the home page opens with the Hero's scroll-driven frame sequence —
// the nav stays hidden until that animation reaches its last frame.
const useHeroRevealed = (enabled: boolean) => {
  const [revealed, setRevealed] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setRevealed(true);
      return;
    }
    setRevealed(false);

    let ticking = false;
    const check = () => {
      const heroEl = document.getElementById("hero-section");
      if (!heroEl) {
        setRevealed(true);
        return;
      }
      const range = heroEl.offsetHeight - window.innerHeight;
      const progress = range > 0 ? Math.max(0, Math.min(1, -heroEl.getBoundingClientRect().top / range)) : 1;
      setRevealed(progress >= 0.995);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        check();
        ticking = false;
      });
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled]);

  return revealed;
};

// While the fixed nav band is scrolled over one of these dark sections, it
// switches to a glass look, then reverts to solid black once past it.
const NAV_BAND_Y = 50;
const GLASS_SECTION_IDS = ["awards-section", "expedition-cta-section"];

const useIsOverDarkSection = () => {
  const [isOver, setIsOver] = useState(false);

  useEffect(() => {
    let ticking = false;
    const check = () => {
      const isOverAny = GLASS_SECTION_IDS.some((id) => {
        const el = document.getElementById(id);
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top <= NAV_BAND_Y && rect.bottom >= NAV_BAND_Y;
      });
      setIsOver(isOverAny);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        check();
        ticking = false;
      });
    };

    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return isOver;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const revealed = useHeroRevealed(location.pathname === "/");
  const isOverDarkSection = useIsOverDarkSection();

  return (
    <CardNav
      logo={logo}
      logoAlt="Mysher Aviary"
      items={NAV_ITEMS}
      baseColor={isOverDarkSection ? "rgba(255, 255, 255, 0.14)" : "#0a0908"}
      menuColor="#fff"
      buttonBgColor="#f5a623"
      buttonTextColor="#171310"
      buttonLabel="Book a Visit"
      onButtonClick={() => navigate("/contact")}
      className={`${revealed ? "" : "nav-hidden"} ${isOverDarkSection ? "glass" : ""}`}
    />
  );
};

export default Header;
